import { useState, useRef, useEffect, useCallback } from 'react';
import { assessmentsAPI } from '../lib/api';

// Encapsulates all assessment anti-cheat / biometric monitoring so the page
// component stays lean and the event-listener effects have correct, stable deps.
export function useAssessmentSecurity({ started, submitted, attemptIdRef, currentQ }) {
  const [antiCheatCounts, setAntiCheatCounts] = useState({ tab_switch: 0, copy_paste: 0, right_click: 0 });
  const [showWarning, setShowWarning] = useState(false);
  const [warningMsg, setWarningMsg] = useState('');
  const keystrokesRef = useRef([]);

  // Keep latest question index in a ref so logCheatEvent stays referentially stable.
  const currentQRef = useRef(currentQ);
  currentQRef.current = currentQ;

  const logCheatEvent = useCallback(async (type, details) => {
    if (!attemptIdRef.current) return;
    try {
      await assessmentsAPI.logAntiCheat(attemptIdRef.current, {
        type, details, question_index: currentQRef.current,
      });
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Anti-cheat log failed:', err);
    }
  }, [attemptIdRef]);

  // Biometric: capture keystroke timing signature
  useEffect(() => {
    if (!started || submitted) return;
    const handleKey = (e) => {
      keystrokesRef.current.push({ key: e.key, timestamp: Date.now() });
      if (keystrokesRef.current.length > 500) keystrokesRef.current = keystrokesRef.current.slice(-500);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [started, submitted]);

  // Tab switch / window blur
  useEffect(() => {
    if (!started || submitted) return;
    const handleVisibility = () => {
      if (document.hidden) {
        logCheatEvent('tab_switch', 'Switched away from assessment tab');
        setAntiCheatCounts(prev => ({ ...prev, tab_switch: prev.tab_switch + 1 }));
        setWarningMsg('Tab switch detected! This event has been logged.');
        setShowWarning(true);
      }
    };
    const handleBlur = () => logCheatEvent('window_blur', 'Window lost focus');
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('blur', handleBlur);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('blur', handleBlur);
    };
  }, [started, submitted, logCheatEvent]);

  // Copy / paste / cut / right-click
  useEffect(() => {
    if (!started || submitted) return;
    const handleCopyPaste = (e) => {
      const type = e.type;
      logCheatEvent('copy_paste', `${type} event detected`);
      setAntiCheatCounts(prev => ({ ...prev, copy_paste: prev.copy_paste + 1 }));
      setWarningMsg(`${type.charAt(0).toUpperCase() + type.slice(1)} detected! This event has been logged.`);
      setShowWarning(true);
    };
    const handleContextMenu = (e) => {
      e.preventDefault();
      logCheatEvent('right_click', 'Right-click context menu attempted');
      setAntiCheatCounts(prev => ({ ...prev, right_click: prev.right_click + 1 }));
      setWarningMsg('Right-click is disabled during the assessment.');
      setShowWarning(true);
    };
    document.addEventListener('copy', handleCopyPaste);
    document.addEventListener('paste', handleCopyPaste);
    document.addEventListener('cut', handleCopyPaste);
    document.addEventListener('contextmenu', handleContextMenu);
    return () => {
      document.removeEventListener('copy', handleCopyPaste);
      document.removeEventListener('paste', handleCopyPaste);
      document.removeEventListener('cut', handleCopyPaste);
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [started, submitted, logCheatEvent]);

  return { antiCheatCounts, showWarning, setShowWarning, warningMsg, keystrokesRef };
}
